import { FieldFunctionOptions } from '@apollo/client';
import walk from 'Helpers/walk';
import sensitiveFields from 'Generated/sensitiveFields.generated.json';
import set from 'lodash/set';
import get from 'lodash/get';
import { History } from 'history';
import { DEFAULT_SENSITIVE_VALUE } from 'Source/constants';
import { NotificationsOutput, Severity } from 'Generated/schema';
import intersection from 'lodash/intersection';
import { LocationErrorState } from 'Components/utils/ApiErrorFallback';

export const endlessPaginationDefaultMerge =
  <T extends Record<any, any>>(
    field: keyof T,
    getPaginationKey = (variables: Record<string, any>) => variables.input?.exclusiveStartKey,
  ) =>
  (existing: T, incoming: T, { variables }: FieldFunctionOptions): T => {
    // if no data exists, fetch it from the server
    if (!existing) {
      return incoming;
    }

    // if data exists, but we are refetching the first page, then clear everything and just
    // keep this first page
    if (!getPaginationKey(variables)) {
      return incoming;
    }

    // if data exists and we are fetching some page, append its results to the existing set
    const existingPaginatedItems = existing[field] as Array<any>;
    const incomingPaginatedItems = incoming[field] as Array<any>;
    return {
      ...incoming,
      [field]: [...existingPaginatedItems, ...incomingPaginatedItems],
    };
  };

export const mergeNotifications = (
  existing: NotificationsOutput,
  incoming: NotificationsOutput,
  { readField, mergeObjects }: FieldFunctionOptions,
): NotificationsOutput => {
  if (!existing) {
    return incoming;
  }

  const existingItems = existing?.edges;
  const incomingItems = incoming?.edges;

  const merged: any[] = existingItems ? existingItems.slice(0) : [];
  const notificationsToIndex: Record<string, number> = Object.create(null);

  if (existingItems) {
    existingItems.forEach((notification, index) => {
      notificationsToIndex[readField<string>('id', notification.node)] = index;
    });
  }

  incomingItems.forEach(notification => {
    const notificationId = readField<string>('id', notification.node);
    const index = notificationsToIndex[notificationId];
    if (typeof index === 'number') {
      // Merge the new notification data with the existing notification data.
      merged[index] = mergeObjects(merged[index], notification);
    } else {
      // First time we've seen this notification in this array.
      notificationsToIndex[notificationId] = merged.length;
      merged.push(notification);
    }
  });

  return {
    ...incoming,
    edges: merged,
  };
};

export const redactSensitiveData = (
  data: Record<string, unknown>,
  redactValue = DEFAULT_SENSITIVE_VALUE,
) => {
  const payloadPaths: string[] = [];
  const sensitiveFieldNameSet = new Set<string>();

  walk(data, path => payloadPaths.push(path));
  walk(sensitiveFields, path => sensitiveFieldNameSet.add(get(sensitiveFields, path)));

  // In order to know which data to redact we compare the value of leaf keys between the actual
  // payload and the sensitive paths which are in the style of `{type}.{field}`. This guarantees
  // that sensitive fields will be redacted, but it will mean that some non-sensitive fields with
  // the same name as some sensitive fields may be redacted as well.
  // For now, we are ok with this since handling this scenario would be too much effort (it's not
  // easy to get the necessary data for this)
  const redactedFieldNameList = [...sensitiveFieldNameSet];
  const pathsToRedact = payloadPaths.filter(payloadPath => {
    return redactedFieldNameList.some(redactedFieldName => payloadPath.includes(redactedFieldName));
  });

  pathsToRedact.forEach(pathToRedact => {
    set(data, pathToRedact, redactValue);
  });

  return data;
};

export const updatePageStatus = (
  history: History<Record<string, any>>,
  statusCode: LocationErrorState['statusCode'],
) => {
  // Only update the page status if we're changing the status. Else we end up in an endless loop where the URL
  // continuously changes which means that React continuously unmounts & remounts the component
  if (history.location?.state?.statusCode !== statusCode) {
    history.replace({
      ...history.location,
      state: { ...history.location.state, statusCode },
    });
  }
};

export const sortSeverities = (severities: Severity[]) => {
  return intersection(Object.values(Severity), severities);
};
